<template>
  <footer class="bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
    <div class="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
      <div class="grid grid-cols-1 md:grid-cols-4 gap-8">
        <!-- Brand Section -->
        <div class="col-span-1 md:col-span-2">
          <div class="flex items-center">
            <svg
              class="w-8 h-8 text-primary-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M13 10V3L4 14h7v7l9-11h-7z"
              />
            </svg>
            <span class="ml-2 text-xl font-bold text-gray-900 dark:text-gray-100">
              MCP Tools Collection
            </span>
          </div>
          <p class="mt-4 text-gray-600 dark:text-gray-400 max-w-md">
            AI-Powered Modular Architecture with Telegram Authentication. Built with love by
            Profikid using Cloudflare Workers and MCP.
          </p>
          <div class="mt-6 flex space-x-6">
            <a
              href="https://github.com/profikid"
              target="_blank"
              rel="noopener noreferrer"
              class="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
            >
              <span class="sr-only">GitHub</span>
              <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path
                  fill-rule="evenodd"
                  d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z"
                  clip-rule="evenodd"
                />
              </svg>
            </a>
            <a
              href="https://twitter.com/profikid"
              target="_blank"
              rel="noopener noreferrer"
              class="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
            >
              <span class="sr-only">Twitter</span>
              <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path
                  d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84"
                />
              </svg>
            </a>
          </div>
        </div>

        <!-- Features -->
        <div>
          <h3
            class="text-sm font-semibold text-gray-900 dark:text-gray-100 tracking-wider uppercase"
          >
            Features
          </h3>
          <ul class="mt-4 space-y-4">
            <li>
              <span
                class="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
              >
                Web Search Tools
              </span>
            </li>
            <li>
              <span
                class="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
              >
                Calculator & Math
              </span>
            </li>
            <li>
              <span
                class="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
              >
                Vector Storage
              </span>
            </li>
            <li>
              <span
                class="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
              >
                Telegram Integration
              </span>
            </li>
          </ul>
        </div>

        <!-- Support -->
        <div>
          <h3
            class="text-sm font-semibold text-gray-900 dark:text-gray-100 tracking-wider uppercase"
          >
            Support
          </h3>
          <ul class="mt-4 space-y-4">
            <li>
              <a
                href="mailto:support@profikid.nl"
                class="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
              >
                Contact Support
              </a>
            </li>
            <li>
              <a
                href="https://github.com/profikid/mcp-tools/docs"
                target="_blank"
                rel="noopener noreferrer"
                class="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
              >
                Documentation
              </a>
            </li>
            <li>
              <a
                href="https://status.profikid.nl"
                target="_blank"
                rel="noopener noreferrer"
                class="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
              >
                System Status
              </a>
            </li>
          </ul>
        </div>
      </div>

      <!-- Bottom Section -->
      <div class="mt-12 border-t border-gray-200 dark:border-gray-700 pt-8">
        <div class="flex flex-col md:flex-row justify-between items-center">
          <div class="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
            <span>&copy; {{ currentYear }} Profikid. All rights reserved.</span>
            <span class="hidden md:inline">•</span>
            <a
              href="/privacy"
              class="hover:text-gray-900 dark:hover:text-gray-100"
            >Privacy Policy</a>
            <span>•</span>
            <a
              href="/terms"
              class="hover:text-gray-900 dark:hover:text-gray-100"
            >Terms of Service</a>
          </div>

          <div class="mt-4 md:mt-0 flex items-center space-x-4">
            <span class="text-sm text-gray-500 dark:text-gray-400"> Powered by </span>
            <div class="flex items-center space-x-2">
              <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Cloudflare Workers</span>
              <span class="text-gray-400">•</span>
              <span class="text-sm font-medium text-gray-700 dark:text-gray-300">MCP</span>
            </div>
          </div>
        </div>

        <!-- Version Info -->
        <div class="mt-4 text-center">
          <span class="text-xs text-gray-400 dark:text-gray-500">
            Version 1.0.0 | Built with ❤️ for the AI community
          </span>
        </div>
      </div>
    </div>
  </footer>
</template>

<script setup lang="ts">
import { computed } from 'vue';

const currentYear = computed(() => new Date().getFullYear());
</script>

<style scoped>
/* Ensure footer styling is consistent */
footer {
  margin-top: auto;
}

/* Link hover effects */
a {
  transition: color 0.15s ease-in-out;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .grid {
    gap: 1.5rem;
  }
}

/* Print styles */
@media print {
  footer {
    display: none;
  }
}
</style>
